import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Ban,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Plus,
  UserPlus,
  Loader2,
  Activity,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import api from '../lib/api';
import Modal from '../components/ui/Modal';
import UserActivityDrawer from '../components/users/UserActivityDrawer';

interface AdminUser {
  _id: string;
  email: string;
  role: string;
  isBanned?: boolean;
  isActive?: boolean;
  profile?: { name?: string };
  subscription?: { type?: string; isActive?: boolean };
}

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin' as 'admin' | 'moderator',
  });

  const mutation = useMutation({
    mutationFn: async (data: typeof form) => {
      await api.post('/admin/create-user', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User created');
      setForm({ name: '', email: '', password: '', role: 'admin' });
      onClose();
    },
    onError: (err) =>
      toast.error(
        isAxiosError(err)
          ? err.response?.data?.error?.message || err.message
          : 'Failed to create user',
      ),
  });

  return (
    <Modal open={open} onClose={onClose} title="Create Admin / Moderator">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate(form);
        }}
        className="space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input-field"
            required
            minLength={2}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="input-field"
            required
            minLength={12}
            placeholder="Min 12 chars, upper+lower+num+special"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'moderator' })}
            className="input-field"
          >
            <option value="admin">Admin</option>
            <option value="moderator">Moderator</option>
          </select>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <UserPlus size={16} />
            )}
            Create
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CreateStudentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  const mutation = useMutation({
    mutationFn: async (data: typeof form) => {
      await api.post('/users/create', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Student account created');
      setForm({ name: '', email: '', password: '' });
      onClose();
    },
    onError: (err) =>
      toast.error(
        isAxiosError(err)
          ? err.response?.data?.error?.message || err.message
          : 'Failed to create student',
      ),
  });

  return (
    <Modal open={open} onClose={onClose} title="Create Student">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate(form);
        }}
        className="space-y-4"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="input-field"
            required
            minLength={2}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input-field"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="input-field"
            required
            minLength={8}
            placeholder="Min 8 chars, upper+lower+num+special"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary flex items-center gap-2"
          >
            {mutation.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <UserPlus size={16} />
            )}
            Create
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function UsersPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [activityUser, setActivityUser] = useState<AdminUser | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search],
    queryFn: async () => {
      const res = await api.get('/users', { params: { page, limit: 20, search } });
      return res.data;
    },
  });

  const banMutation = useMutation({
    mutationFn: async ({ userId, isBanned }: { userId: string; isBanned: boolean }) => {
      if (isBanned) {
        await api.patch(`/users/${userId}/unban`);
      } else {
        await api.patch(`/users/${userId}/ban`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User updated');
    },
    onError: () => toast.error('Failed to update user'),
  });

  const activateMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.patch(`/users/${userId}/activate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User activated');
    },
    onError: () => toast.error('Failed to activate user'),
  });

  const users = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 mt-1">{pagination.total} total users</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCreateStudent(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <UserPlus size={16} /> Create Student
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} /> Create Admin
          </button>
        </div>
      </div>

      <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} />
      <CreateStudentModal open={showCreateStudent} onClose={() => setShowCreateStudent(false)} />

      {activityUser && (
        <UserActivityDrawer
          open={!!activityUser}
          onClose={() => setActivityUser(null)}
          userId={activityUser._id}
          userLabel={activityUser.profile?.name || activityUser.email}
        />
      )}

      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="input-field pl-9"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-500">User</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Email</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Role</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Subscription</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user: AdminUser) => (
                  <tr key={user._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-700 text-xs font-medium">
                            {user.profile?.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <span className="font-medium text-gray-900">
                          {user.profile?.name || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{user.email}</td>
                    <td className="py-3 px-4">
                      <span className="badge bg-primary-50 text-primary-700">{user.role}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`badge ${user.subscription?.isActive ? 'bg-success-50 text-success-700' : 'bg-gray-100 text-gray-600'}`}
                      >
                        {user.subscription?.type || 'free'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {user.isBanned ? (
                        <span className="badge bg-danger-50 text-danger-700">Banned</span>
                      ) : user.isActive === false ? (
                        <span className="badge bg-amber-50 text-amber-700">Inactive</span>
                      ) : (
                        <span className="badge bg-success-50 text-success-700">Active</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {user.isActive === false && (
                          <button
                            onClick={() => activateMutation.mutate(user._id)}
                            disabled={activateMutation.isPending}
                            className="text-xs px-2 py-1 rounded flex items-center gap-1 text-success-700 hover:bg-success-50"
                          >
                            <Check size={14} /> Activate
                          </button>
                        )}
                        <button
                          onClick={() => setActivityUser(user)}
                          className="text-xs px-2 py-1 rounded flex items-center gap-1 text-primary-700 hover:bg-primary-50"
                        >
                          <Activity size={14} /> Activity
                        </button>
                        <button
                          onClick={() =>
                            banMutation.mutate({ userId: user._id, isBanned: !!user.isBanned })
                          }
                          className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${user.isBanned ? 'text-success-700 hover:bg-success-50' : 'text-danger-700 hover:bg-danger-50'}`}
                        >
                          {user.isBanned ? (
                            <>
                              <ShieldCheck size={14} /> Unban
                            </>
                          ) : (
                            <>
                              <Ban size={14} /> Ban
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.totalPages}
              className="btn-secondary text-xs flex items-center gap-1"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
